import { useState, useEffect, useRef, useCallback } from 'react';
import { loadDeepARWithCORSWorkaround } from './deepARLoader';
import { DEEPAR_LICENSE_KEY } from '../../../constants';

// Helper function to load DeepAR script
const loadDeepARScript = () => {
  // Use the CORS workaround loader
  return loadDeepARWithCORSWorkaround();
};

// Helper functions for localStorage
const getLongDistanceTrackingState = (eventId) => {
  if (!eventId) return false;
  try {
    const key = `deepar_long_distance_tracking_${eventId}`;
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : false;
  } catch (error) {
    console.warn('Failed to load long distance tracking state from localStorage:', error);
    return false;
  }
};

const saveLongDistanceTrackingState = (eventId, state) => {
  if (!eventId) return;
  try {
    const key = `deepar_long_distance_tracking_${eventId}`;
    localStorage.setItem(key, JSON.stringify(state));
  } catch (error) {
    console.warn('Failed to save long distance tracking state to localStorage:', error);
  }
};

export const useDeepAR = ({ canvasWidth = 1280, canvasHeight = 720, eventId = null } = {}) => {
  const [deepAR, setDeepAR] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentEffect, setCurrentEffect] = useState(null);
  const [isInitializing, setIsInitializing] = useState(false);
  // Initialize long distance tracking with saved state
  const [longDistanceTracking, setLongDistanceTracking] = useState(() => getLongDistanceTrackingState(eventId));
  const canvasRef = useRef(null);
  const initializationPromiseRef = useRef(null);

  // Initialize DeepAR
  const initializeDeepAR = useCallback(async () => {
    if (!DEEPAR_LICENSE_KEY || !canvasRef.current) return;

    // Prevent multiple initializations
    if (isInitializing || initializationPromiseRef.current) {
      return initializationPromiseRef.current;
    }

    // Shutdown existing instance if it exists
    if (deepAR) {
      try {
        deepAR.shutdown();
      } catch (err) {
        console.warn('Error shutting down DeepAR:', err);
      }
      setDeepAR(null);
      setIsInitialized(false);
    }

    setIsInitializing(true);
    setIsLoading(true);
    setError(null);

    const initPromise = (async () => {
      try {
        // Load DeepAR using our helper function
        const deepar = await loadDeepARScript();
        
        // Listen for console messages to detect license validation
        const originalConsoleInfo = console.info;
        let licenseIsValid = true;
        console.info = (...args) => {
          const message = args.join(' ');
          if (message.includes('License key not valid')) {
            licenseIsValid = false;
          }
          originalConsoleInfo.apply(console, args);
        };
        
        // Try different initialization approaches
        let deepARInstance;
        
        try {
          // Build initialization configuration
          const initConfig = {
            licenseKey: DEEPAR_LICENSE_KEY,
            canvas: canvasRef.current,
            effect: ''
          };

          // Add long distance face tracking if enabled
          if (longDistanceTracking) {
            initConfig.additionalOptions = {
              hint: "enableFaceTrackingCnn"
            };
          }

          // Minimal configuration to avoid WASM loading issues
          deepARInstance = await deepar.initialize(initConfig);
        } catch (err) {
          console.warn('Minimal initialization also failed:', err);
          throw err;
        }

        // Restore console
        console.info = originalConsoleInfo;

        if (!licenseIsValid) {
          console.error('DeepAR license key is invalid');
          setError({
            message: 'Invalid DeepAR license key',
            suggestions: [
              'Enter a valid license key from developer.deepar.ai',
              'Make sure the license is registered for your domain',
              'Check if the license key is correctly formatted'
            ]
          });
          setDeepAR(null);
          setIsInitialized(false);
          if (deepARInstance) {
            try {
              deepARInstance.shutdown();
            } catch (e) {
              console.warn('Error shutting down invalid instance:', e);
            }
          }
        } else {
          // Add face tracking event listener to fix scaling issues
          if (deepARInstance.callbacks) {
            deepARInstance.callbacks.onFaceVisibilityChanged = (visible) => {
              if (visible && canvasRef.current) {
                // Reset canvas dimensions when face is re-detected
                setTimeout(() => {
                  if (canvasRef.current) {
                    const canvas = canvasRef.current;
                    const rect = canvas.getBoundingClientRect();
                    canvas.width = rect.width;
                    canvas.height = rect.height;
                  }
                }, 100);
              }
            };
          }
          
          setDeepAR(deepARInstance);
          setIsInitialized(true);
          setError(null);
        }
      } catch (err) {
        console.error('Failed to initialize DeepAR:', err);
        console.error('License key used:', DEEPAR_LICENSE_KEY.substring(0, 8) + '...');
        
        // Provide more specific error messages and solutions
        let errorMessage = 'Failed to initialize DeepAR';
        let suggestions = [];
        
        if (err.message.includes('404') || err.message.includes('Not Found')) {
          errorMessage = 'DeepAR resources not found';
          suggestions = [
            'The DeepAR CDN files are not accessible',
            'Try refreshing the page',
            'Check your internet connection',
            'This might be a temporary CDN issue'
          ];
        } else if (err.message.includes('CORS') || err.message.includes('cross-origin')) {
          errorMessage = 'CORS error loading DeepAR resources';
          suggestions = [
            'Check your internet connection',
            'Try refreshing the page',
            'Contact support if the issue persists'
          ];
        } else if (err.message.includes('WebAssembly') || err.message.includes('wasm')) {
          errorMessage = 'WebAssembly loading failed';
          suggestions = [
            'Your browser might not support WebAssembly',
            'Try using a modern browser (Chrome, Firefox, Safari, Edge)',
            'Check if WebAssembly is enabled in your browser',
            'The WASM files might be corrupted or inaccessible'
          ];
        } else if (err.message.includes('currently being initialized')) {
          errorMessage = 'Multiple initialization attempts detected';
          suggestions = [
            'DeepAR is already initializing',
            'Please wait for the current initialization to complete',
            'Try refreshing the page if this persists'
          ];
        } else if (err.message.includes('Failed to fetch') || err.message.includes('net::ERR_FAILED')) {
          errorMessage = 'Network error loading DeepAR library';
          suggestions = [
            'Check your internet connection',
            'Try a different network if possible',
            'Check if your firewall is blocking the connection',
            'Try refreshing the page'
          ];
        } else if (err.message.includes('License') || err.message.includes('license')) {
          errorMessage = 'Invalid license key';
          suggestions = [
            'Enter a valid DeepAR license key',
            'Get a license key from developer.deepar.ai',
            'Make sure the license key is registered for your domain'
          ];
        } else {
          errorMessage = `DeepAR initialization failed: ${err.message}`;
          suggestions = [
            'Try refreshing the page',
            'Check your internet connection',
            'Try a different browser',
            'Contact support if the issue persists'
          ];
        }
        
        setError({ message: errorMessage, suggestions });
      } finally {
        setIsLoading(false);
        setIsInitializing(false);
        initializationPromiseRef.current = null;
      }
    })();

    initializationPromiseRef.current = initPromise;
    return initPromise;
  }, [deepAR, isInitializing, longDistanceTracking]);

  // Toggle long distance face tracking
  const toggleLongDistanceTracking = useCallback(async (event) => {
    const enabled = event?.target?.checked ?? !longDistanceTracking;
    
    if (longDistanceTracking === enabled) return;
    
    setLongDistanceTracking(enabled);
    
    // Save state to localStorage
    saveLongDistanceTrackingState(eventId, enabled);
    
    // Reinitialize DeepAR with new settings
    if (isInitialized) {
      await initializeDeepAR();
    }
  }, [longDistanceTracking, isInitialized, initializeDeepAR, eventId]);

  // Load effect
  const loadEffect = useCallback(async (effectPath) => {
    if (!deepAR || !isInitialized) return;

    try {
      setIsLoading(true);
      setError(null);
      
      let effectData = effectPath;
      
      // If it's a data URL (base64), convert to ArrayBuffer
      if (effectPath.startsWith('data:')) {
        const base64Data = effectPath.split(',')[1];
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        effectData = bytes.buffer;
      }
      
      // Load effect using DeepAR switchEffect
      await deepAR.switchEffect(effectData);
      setCurrentEffect(effectPath);
      
      // Force canvas dimension reset after loading effect
      if (canvasRef.current) {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width;
        canvas.height = rect.height;
      }
    } catch (err) {
      console.error('Failed to load effect:', err);
      const errorMsg = `Failed to load effect: ${effectPath}`;
      setError(errorMsg);
      // Don't set current effect if loading failed
      setCurrentEffect(null);
    } finally {
      setIsLoading(false);
    }
  }, [deepAR, isInitialized]);

  // Clear effect
  const clearEffect = useCallback(async () => {
    if (!deepAR || !isInitialized) return;

    try {
      // Clear by switching to empty effect using clearEffect method
      deepAR.clearEffect();
      setCurrentEffect(null);
      
      // Force canvas dimension reset after clearing effect
      if (canvasRef.current) {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width;
        canvas.height = rect.height;
      }
    } catch (err) {
      console.error('Failed to clear effect:', err);
    }
  }, [deepAR, isInitialized]);

  // Set camera stream
  const setVideoElement = useCallback((video) => {
    if (!deepAR || !isInitialized) return;

    try {
      deepAR.setVideoElement(video, true);
    } catch (err) {
      console.error('Failed to set video element:', err);
    }
  }, [deepAR, isInitialized]);

  // Resize canvas
  const resizeCanvas = useCallback((width, height) => {
    if (!deepAR || !canvasRef.current) return;

    const canvas = canvasRef.current;
    canvas.width = width;
    canvas.height = height;
  }, [deepAR]);

  // Start/stop rendering - these methods may not exist in current DeepAR API
  const startRendering = useCallback(() => {
    if (!deepAR) return;
    // Method may not be needed in current API
  }, [deepAR]);

  const stopRendering = useCallback(() => {
    if (!deepAR) return;
    // Method may not be needed in current API
  }, [deepAR]);

  // Preload DeepAR script on mount
  useEffect(() => {
    // Start loading the script early
    loadDeepARScript().catch(err => {
      console.warn('Failed to preload DeepAR:', err);
    });
  }, []);

  // Initialize on mount
  useEffect(() => {
    if (DEEPAR_LICENSE_KEY && canvasRef.current && !isInitialized && !isInitializing && !initializationPromiseRef.current) {
      initializeDeepAR();
    }

    return () => {
      if (deepAR) {
        try {
          deepAR.shutdown();
        } catch (err) {
          console.warn('Error during cleanup:', err);
        }
      }
      // Clear the initialization promise on cleanup
      initializationPromiseRef.current = null;
    };
  }, [initializeDeepAR, isInitialized, isInitializing, deepAR]);

  // Update long distance tracking when eventId changes
  useEffect(() => {
    const savedState = getLongDistanceTrackingState(eventId);
    if (savedState !== longDistanceTracking) {
      setLongDistanceTracking(savedState);
    }
  }, [eventId, longDistanceTracking]);

  return {
    deepAR,
    canvasRef,
    isInitialized,
    isLoading: isLoading || isInitializing,
    error,
    currentEffect,
    longDistanceTracking,
    toggleLongDistanceTracking,
    loadEffect,
    clearEffect,
    setVideoElement,
    resizeCanvas,
    startRendering,
    stopRendering,
    initializeDeepAR
  };
};